import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "body" }
const _hoisted_2 = {
  key: 0,
  class: "form cancellation"
}
const _hoisted_3 = { class: "xfields" }
const _hoisted_4 = { class: "form buttons" }
const _hoisted_5 = { class: "buttons-wrapper" }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = {
  key: 3,
  class: "warning"
}
const _hoisted_9 = ["href"]

import { reactive, computed, ref, onMounted } from "vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import type { FieldSchema, FormSchema } from "@/models/form";
import type { LOSType } from "@/models/opal";
import Modal from "@/components/shared/Modal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import { $api } from "@/services/api";
import Error from "@/components/shared/Error.vue";
import useValidation from "@/use/validation";
import { ApplicationStatusTypeIds } from "@/config/constants";
import { ProductIds } from "@/config/constants";
import useApplicationData from "@/composables/useApplicationData";

interface Props {
  application: any;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CancellationModal',
  props: {
    application: {}
  },
  emits: ["closeCancellationForm", "cancelApplication"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;
const store = useStore();
const route = useRoute();
const props = __props;
const { emailAddress } = useApplicationData();

const cancellationReasons = computed(
  () => store.state.lookupsModule.lookups["applicationStatusReasons"],
);
const submittingRequest = ref<boolean>(false);
const submitted = ref<boolean>(false);
const submissionError = ref<boolean>(false);
const rtcPeriodEndedError = ref<boolean>(false);
const rtcPeriodEndedErrorMessage = ref<string>("");
const { validateForm } = useValidation();

let info = reactive({
  cancellationReasonId: undefined,
  cancellationReason: undefined,
} as any);

const confirmationText = ref<string>(
  "Your cancellation request has been submitted. You will be notified via email once the request is completed. It usually takes 2 to 3 business days.",
);
const cancellationReasonOptions = computed(() =>
  (cancellationReasons.value ?? []).map((sr: LOSType) => {
    return {
      label: sr.description,
      value: sr.id,
    };
  }),
);

const productTypeId = computed(() => store.state.appData?.application?.productTypeId || props.application.productTypeId);
const applicationStatusId = computed(() => store.state.appData?.application?.applicationStatusId || props.application.applicationStatusId);
const referenceId = computed<string>(
  () =>
    (route?.params?.referenceId as string) ||
    store.getters["modal/cancellationReferenceId"] ||
    "",
);
const applicationInRTCPeriod = computed(
  () => applicationStatusId.value === ApplicationStatusTypeIds.RIGHT_TO_CANCEL,
);
const applicationRTCPeriodFound = ref<boolean>(false);

const checkRTCPeriodOnMounted = computed<boolean>(
  () =>
    (!applicationInRTCPeriod.value && productTypeId.value === ProductIds.REFI) ||
    (!applicationInRTCPeriod.value && productTypeId.value === ProductIds.PEL),
);

const cancellationFormSchema = reactive({
  cancellationReasonId: {
    name: "cancellationReasonId",
    type: "select",
    label: "Cancellation Reason",
    errorMessage: "A cancellation reason is required",
    hideLabel: true,
    required: true,
  } as FieldSchema,
  comment: {
    name: "comment",
    type: "textarea",
    hideLabel: true,
    required: false,
  } as FieldSchema,
}) as FormSchema;

function handleReasonChange(evt: any) {
  const statusReasonName =
    cancellationReasons.value.find((cr: any) => cr.id === evt.cancellationReasonId)?.name || null;
  info.cancellationReason = statusReasonName;
}
function resetForm() {
  info.cancellationReasonId = undefined;
  info.cancellationReason = undefined;
}
function closeCancellationModal() {
  if (submitted.value && !submissionError.value) {
    emit("cancelApplication");
  }
  store.dispatch("modal/closeCancellationModal");
  resetForm();
}

async function submitForm() {
  submissionError.value = false;
  submittingRequest.value = true;

  const valid = validateForm(cancellationFormSchema, {
    cancellationReasonId: info.cancellationReasonId,
  });
  if (valid) {
    
    const response = await $api.applications.cancelApplication(
      referenceId.value,
      props.application.lenderId,
      info,
    );
    
    
    if (
      response?.results?.id &&
      response?.results?.statusReasonId &&
      response?.results?.statusReasonName
    ) {
      store.dispatch("setApplication", response.results);
      submitted.value = true;
      submittingRequest.value = false;
    }

    if (response.errors && response?.errors?.length > 0) {
      submittingRequest.value = false;

      // Handle RTC Error
      if (response.errors[0]?.includes("Right to Cancel period ending")) {
        rtcPeriodEndedError.value = true;
        rtcPeriodEndedErrorMessage.value = response.errors[0];
      }
      // Handle all other errors
      else {
        submittingRequest.value = false;
        submissionError.value = true;
      }
    }
  } else {
    submittingRequest.value = false;
  }
}

async function checkRTCPeriod() {
  applicationRTCPeriodFound.value = false;
  rtcPeriodEndedError.value = false;
  rtcPeriodEndedErrorMessage.value = "";

  const response = await $api.applications.checkRTCPeriod(referenceId.value);

  // RTC available app is in RTC period
  if (response.results === "RightToCancelAvailable") {
    applicationRTCPeriodFound.value = true;
  }

  // no RTC found - allow cancellation app is NOT in RTC period
  if (response.results === "RightToCancelNotFound") {
    applicationRTCPeriodFound.value = false;
  }

  // RTC period ended
  if (response.results === "RightToCancelEnded") {
    rtcPeriodEndedError.value = true;
    rtcPeriodEndedErrorMessage.value = response.errors[0];
  }
}

onMounted(() => {
  if (checkRTCPeriodOnMounted.value) {
    checkRTCPeriod();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    class: "cancellation-modal",
    onClose: closeCancellationModal,
    allowClose: submitted.value && !submissionError.value
  }, {
    header: _withCtx(() => [...(_cache[3] || (_cache[3] = [
      _createTextVNode("Let us know the reason for cancelling your application?", -1)
    ]))]),
    footer: _withCtx(() => [...(_cache[7] || (_cache[7] = []))]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (!submitted.value && !submissionError.value)
          ? (_openBlock(), _createElementBlock("form", _hoisted_2, [
              _createElementVNode("fieldset", null, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(BaseFormField, {
                    schema: cancellationFormSchema.cancellationReasonId,
                    options: cancellationReasonOptions.value,
                    modelValue: _unref(info).cancellationReasonId,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(info).cancellationReasonId) = $event)),
                    modelModifiers: { number: true },
                    disabled: submittingRequest.value || rtcPeriodEndedError.value,
                    onChange: _cache[1] || (_cache[1] = ($event: any) => (handleReasonChange(_unref(info))))
                  }, {
                    "default-option": _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("Select the reason", -1)
                    ]))]),
                    _: 1
                  }, 8, ["schema", "options", "modelValue", "disabled"]),
                  _createVNode(BaseFormField, {
                    class: "hide comment-area",
                    modelValue: _unref(info).comment,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(info).comment) = $event)),
                    schema: cancellationFormSchema.comment
                  }, null, 8, ["modelValue", "schema"])
                ])
              ]),
              _createElementVNode("div", _hoisted_4, [
                (applicationInRTCPeriod.value || applicationRTCPeriodFound.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _cache[5] || (_cache[5] = _createElementVNode("p", { class: "warning" }, " Are you sure you want to cancel this application? This action cannot be undone. ", -1)),
                      _createElementVNode("div", _hoisted_5, [
                        _createVNode(NextButton, {
                          text: "Yes",
                          onClick: _withModifiers(submitForm, ["prevent"]),
                          loading: submittingRequest.value
                        }, null, 8, ["loading"]),
                        _createVNode(NextButton, {
                          text: "No",
                          onClick: _withModifiers(closeCancellationModal, ["prevent"])
                        })
                      ])
                    ], 64))
                  : (_openBlock(), _createBlock(NextButton, {
                      key: 1,
                      text: "Submit",
                      onClick: _withModifiers(submitForm, ["prevent"]),
                      loading: submittingRequest.value,
                      disabled: submittingRequest.value || rtcPeriodEndedError.value
                    }, null, 8, ["loading", "disabled"]))
              ])
            ]))
          : _createCommentVNode("", true),
        (submitted.value && !submissionError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(confirmationText.value), 1))
          : _createCommentVNode("", true),
        (submitted.value && submissionError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(Error)
            ]))
          : _createCommentVNode("", true),
        (rtcPeriodEndedError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createTextVNode(_toDisplayString(rtcPeriodEndedErrorMessage.value) + ". Please email our support team at ", 1),
              _createElementVNode("a", {
                href: `mailto:${_unref(emailAddress)}`
              }, _toDisplayString(_unref(emailAddress)), 9, _hoisted_9),
              _cache[6] || (_cache[6] = _createTextVNode(" with any questions or concerns.” ", -1))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["allowClose"]))
}
}

})